import { Injectable, inject } from '@angular/core';

import { SportsCacheService } from '@frontend/sports/betting-offer/feature/sports-cache';
import { SportIntervalCounts } from '@frontend/sports/calendar/data-access';
import { ClientCachingConfig } from '@frontend/sports/common/client-config-data-access';
import { CountItem } from '@frontend/sports/common/core/data-access/sport-model';
import { ExpiringCache } from '@frontend/sports/common/core/utils/expiring-cache';
import { Observable, forkJoin, map } from 'rxjs';

import { CalendarService } from './calendar.service';

@Injectable({
    providedIn: 'root',
})
export class CalendarCacheService {
    private calendarService = inject(CalendarService);
    private sportCacheService = inject(SportsCacheService);

    private readonly clientCachingConfig = inject(ClientCachingConfig);

    private readonly sportItemCache: ExpiringCache = new ExpiringCache(this.clientCachingConfig.sportTimeout);

    getSportWithCalendar(
        id: number,
        competitionId?: number,
        conferenceId?: number,
        includeDynamicCategories?: boolean,
    ): Observable<CountItem | undefined> {
        const sportSource = forkJoin([
            this.sportCacheService.getSport(id, competitionId, conferenceId, includeDynamicCategories),
            this.getSportCalendarCounts(id),
        ]);

        return this.sportItemCache
            .getOrCreate(`sportWithCalendar-${id}${includeDynamicCategories ? `-withDynamicCategories` : ''}`, sportSource)
            .pipe(
                map(([sport, calendar]) => {
                    if (sport) {
                        return {
                            ...sport,
                            meta: {
                                ...sport.meta,
                                calendar: this.isCalendarEnabled(calendar),
                                calendarIntervals: this.getCalendarIntervals(calendar),
                            },
                        };
                    }

                    return;
                }),
            );
    }

    private getSportCalendarCounts(id: number): Observable<SportIntervalCounts | undefined> {
        return this.calendarService.getCalendarCounts(id.toString());
    }

    private isCalendarEnabled(counts: SportIntervalCounts | undefined): boolean {
        return !!(counts?.intervals && Object.keys(counts.intervals).length > 0);
    }

    private getCalendarIntervals(counts: SportIntervalCounts | undefined): string[] {
        if (counts?.intervals) {
            return (
                Object.keys(counts.intervals)
                    .filter((key) => key !== 'live')
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    .map((key) => counts.intervals[key]!.name)
            );
        }

        return [];
    }
}
